import React, {useState} from "react";
import {css} from "@emotion/react";

import {Accordion} from "@pg-design/accordion";
import {Button} from "@pg-design/button-module";
import {borderRadius, mb, mh, mt, onDesktop, ph, pv, w100} from "@pg-design/helpers-css";
import {Loader} from "@pg-design/loader-module";
import {Text} from "@pg-design/text-module";
import {useElementWasVisible} from "@pg-mono/hooks";

import {useElementInteractionObserver} from "../../hooks/use_element_interaction_observer";
import {useGetOfferDetailTransportPoiQuery} from "../../maps/api/get_offer_detail_poi";
import {OpenStreetMapsWithPoi} from "../../maps/components/map_with_poi/OpenStreetMapsWithPoi";
import {useMapNearbyOffersPois} from "../../maps/hooks/use_map_nearby_offers_pois";
import {useTransportPois} from "../../maps/hooks/use_transport_pois";
import {poiAnalytics, PoiGTMModalAction} from "../../maps/tracking/poi_analytics";
import {IPoi} from "../../maps/types/IPoi";
import {getHasCountryPois} from "../../maps/utils/get_has_country_pois";
import {TransportPoiType} from "../../maps/utils/PoiType";
import {OfferDetailLocationSummary} from "../../offer/detail/components/OfferDetailLocationSummary";
import {OfferModalActiveTab} from "../../offer/types/OfferModalActiveTab";
import {OfferType} from "../../offer/types/OfferType";
import {Country} from "../../region/types/Country";
import {ViewType} from "../../view_type/ViewType";
import {PROPERTY_CHAT_BOT_TRIGGER} from "../constants/chat_bot_trigger";
import {OSM_MAP_CONTAINER} from "./PropertyDetailForSale";

interface IProps {
    className?: string;
    offer: {
        id: number;
        name: string;
        type: OfferType;
        geo_area: {
            coordinates: number[][][];
        };
        geo_point: {
            coordinates: [number, number];
        };
        vendor: {
            id: number;
            name: string;
            slug: string;
        };
        address: string;
        location_description: string | null;
        region: {
            country: Country;
            included_location: {
                description: string;
            };
        };
        stats: {
            distance_from_region: {
                center: boolean;
                coords: [number, number];
                region: string;
            };
            ranges_area_min?: number;
            ranges_area_max?: number;
            rooms?: number[];
        } | null;
    };
    geoAreaCoordinates: number[][][];
    geoPointCoordinates: [number, number];
    property?: {
        id: number;
        for_sale: boolean;
    };
    changeModalTab: (tab: OfferModalActiveTab.MAP) => void;
}

export const PropertyDetailLocation = (props: IProps) => {
    const {offerAdditionalPois, defaultPoiTypes, mapBottomSlot} = useMapNearbyOffersPois({
        baseOffer: {
            id: props.offer.id,
            type: props.offer.type,
            stats: {
                ranges_area_min: props.offer.stats?.ranges_area_min,
                ranges_area_max: props.offer.stats?.ranges_area_max,
                rooms: props.offer.stats?.rooms
            }
        },
        showPropertyNumberOnMarker: true
    });

    const offer = {...props.offer, geo_area: {coordinates: {coordinates: props.geoAreaCoordinates}}};

    const hasCountryPois = getHasCountryPois(props.offer.region.country);
    const {data: poiData} = useGetOfferDetailTransportPoiQuery(offer?.id);

    const transportPoisFromApi = (poiData?.poi?.pois.transport || {}) as Record<TransportPoiType, IPoi[]>;

    const {routes} = useTransportPois(transportPoisFromApi, {
        longitude: offer.geo_point.coordinates[0],
        latitude: offer.geo_point.coordinates[1],
        radius: 3000
    });
    const [canMapBeLoaded, setCanMapBeLoaded] = useState(false);

    useElementWasVisible({
        ids: [OSM_MAP_CONTAINER],
        onElementVisible: () => {
            setCanMapBeLoaded(true);
        },
        once: true,
        threshold: 0.05
    });

    const mapWrapperRef = useElementInteractionObserver<HTMLDivElement>({
        callback: () => {
            poiAnalytics.gtm.mapEvent({action: PoiGTMModalAction.MAP_FIRST_INTERACTION});
        },
        once: true
    });

    const openMapModal = () => {
        props.changeModalTab(OfferModalActiveTab.MAP);
    };

    return (
        <>
            <div css={wrapperStyle}>
                <Text variant="headline_4" as="h2" css={headerStyle}>
                    Lokalizacja inwestycji
                </Text>

                <div ref={mapWrapperRef} css={mapWrapperStyle} data-id={OSM_MAP_CONTAINER}>
                    {canMapBeLoaded ? (
                        <OpenStreetMapsWithPoi
                            offer={offer}
                            viewType={ViewType.PROPERTY}
                            initialPoiTypes={defaultPoiTypes}
                            mapConfig={{scrollWheelZoom: false, fitBounds: true, maxZoom: 18}}
                            disablePoiSwitch
                            disableInitiallyOpenedPoiId
                            clusterMarkers
                            customPoiMarkers={offerAdditionalPois}
                            mapBottomSlot={mapBottomSlot}
                            css={mapCustomStyles}
                            onFullscreenClick={openMapModal}
                        />
                    ) : (
                        <Loader />
                    )}
                </div>

                {hasCountryPois && (
                    <div css={contentWrapperStyle}>
                        <OfferDetailLocationSummary {...props} routes={routes} />

                        <Button variant="filled_primary" css={[w100, mt(3)]} onClick={openMapModal}>
                            Sprawdź na mapie
                        </Button>
                    </div>
                )}
            </div>

            <Accordion id={PROPERTY_CHAT_BOT_TRIGGER}>
                <Accordion.Header>
                    <Text as="h3" variant="headline_6">
                        Przeczytaj więcej o lokalizacji
                    </Text>
                </Accordion.Header>
                <Accordion.Content>
                    {props.offer.location_description && (
                        <Text css={[mt(0), preLine]} mb="1.6rem" variant="body_copy_1">
                            {props.offer.location_description}
                        </Text>
                    )}

                    {props.offer.region.included_location?.description && (
                        <Text css={preLine} variant="body_copy_1">
                            {props.offer.region.included_location.description}
                        </Text>
                    )}
                </Accordion.Content>
            </Accordion>
        </>
    );
};

const wrapperStyle = css`
    background: #fff;
    ${borderRadius(2)};
    ${pv(2)};
    ${mb(2)};

    ${onDesktop(css`
        ${pv(3)};
    `)};
`;

const headerStyle = css`
    ${ph(1.5)};
    ${mb(2)};

    ${onDesktop(css`
        ${ph(3)};
    `)};
`;

const mapWrapperStyle = css`
    position: relative;
    height: 52rem;
    display: flex;
    justify-content: center;
    align-items: center;

    ${onDesktop(css`
        height: 41rem;
        ${mh(3)};
    `)};
`;

const contentWrapperStyle = css`
    ${ph(1.5)};

    ${onDesktop(css`
        ${ph(3)};
    `)};
`;

const preLine = css`
    white-space: pre-line;
`;

const mapCustomStyles = css`
    .leaflet-div-icon {
        background-color: transparent;
        border: none;
    }
`;
import React, {useState} from "react";
import {css} from "@emotion/react";

import {Accordion} from "@pg-design/accordion";
import {Button} from "@pg-design/button-module";
import {borderRadius, mb, mh, mt, onDesktop, ph, pv, w100} from "@pg-design/helpers-css";
import {Loader} from "@pg-design/loader-module";
import {Text} from "@pg-design/text-module";
import {useElementWasVisible} from "@pg-mono/hooks";

import {useElementInteractionObserver} from "../../hooks/use_element_interaction_observer";
import {useGetOfferDetailTransportPoiQuery} from "../../maps/api/get_offer_detail_poi";
import {OpenStreetMapsWithPoi} from "../../maps/components/map_with_poi/OpenStreetMapsWithPoi";
import {useMapNearbyOffersPois} from "../../maps/hooks/use_map_nearby_offers_pois";
import {useTransportPois} from "../../maps/hooks/use_transport_pois";
import {poiAnalytics, PoiGTMModalAction} from "../../maps/tracking/poi_analytics";
import {IPoi} from "../../maps/types/IPoi";
import {getHasCountryPois} from "../../maps/utils/get_has_country_pois";
import {TransportPoiType} from "../../maps/utils/PoiType";
import {OfferDetailLocationSummary} from "../../offer/detail/components/OfferDetailLocationSummary";
import {OfferModalActiveTab} from "../../offer/types/OfferModalActiveTab";
import {OfferType} from "../../offer/types/OfferType";
import {Country} from "../../region/types/Country";
import {ViewType} from "../../view_type/ViewType";
import {PROPERTY_CHAT_BOT_TRIGGER} from "../constants/chat_bot_trigger";
import {OSM_MAP_CONTAINER} from "./PropertyDetailForSale";

interface IProps {
    className?: string;
    offer: {
        id: number;
        name: string;
        type: OfferType;
        geo_area: {
            coordinates: number[][][];
        };
        geo_point: {
            coordinates: [number, number];
        };
        vendor: {
            id: number;
            name: string;
            slug: string;
        };
        address: string;
        location_description: string | null;
        region: {
            country: Country;
            included_location: {
                description: string;
            };
        };
        stats: {
            distance_from_region: {
                center: boolean;
                coords: [number, number];
                region: string;
            };
            ranges_area_min?: number;
            ranges_area_max?: number;
            rooms?: number[];
        } | null;
    };
    geoAreaCoordinates: number[][][];
    geoPointCoordinates: [number, number];
    property?: {
        id: number;
        for_sale: boolean;
    };
    changeModalTab: (tab: OfferModalActiveTab.MAP) => void;
}

export const PropertyDetailLocation = (props: IProps) => {
    const {offerAdditionalPois, defaultPoiTypes, mapBottomSlot} = useMapNearbyOffersPois({
        baseOffer: {
            id: props.offer.id,
            type: props.offer.type,
            stats: {
                ranges_area_min: props.offer.stats?.ranges_area_min,
                ranges_area_max: props.offer.stats?.ranges_area_max,
                rooms: props.offer.stats?.rooms
            }
        },
        showPropertyNumberOnMarker: true
    });

    const offer = {...props.offer, geo_area: {coordinates: {coordinates: props.geoAreaCoordinates}}};

    const hasCountryPois = getHasCountryPois(props.offer.region.country);
    const {data: poiData} = useGetOfferDetailTransportPoiQuery(offer?.id);

    const transportPoisFromApi = (poiData?.poi?.pois.transport || {}) as Record<TransportPoiType, IPoi[]>;

    const {routes} = useTransportPois(transportPoisFromApi, {
        longitude: offer.geo_point.coordinates[0],
        latitude: offer.geo_point.coordinates[1],
        radius: 3000
    });
    const [canMapBeLoaded, setCanMapBeLoaded] = useState(false);

    useElementWasVisible({
        ids: [OSM_MAP_CONTAINER],
        onElementVisible: () => {
            setCanMapBeLoaded(true);
        },
        once: true,
        threshold: 0.05
    });

    const mapWrapperRef = useElementInteractionObserver<HTMLDivElement>({
        callback: () => {
            poiAnalytics.gtm.mapEvent({action: PoiGTMModalAction.MAP_FIRST_INTERACTION});
        },
        once: true
    });

    const openMapModal = () => {
        props.changeModalTab(OfferModalActiveTab.MAP);
    };

    return (
        <>
            <div css={wrapperStyle}>
                <Text variant="headline_4" as="h2" css={headerStyle}>
                    Lokalizacja inwestycji
                </Text>

                <div ref={mapWrapperRef} css={mapWrapperStyle} data-id={OSM_MAP_CONTAINER}>
                    {canMapBeLoaded ? (
                        <OpenStreetMapsWithPoi
                            offer={offer}
                            viewType={ViewType.PROPERTY}
                            initialPoiTypes={defaultPoiTypes}
                            mapConfig={{scrollWheelZoom: false, fitBounds: true, maxZoom: 18}}
                            disablePoiSwitch
                            disableInitiallyOpenedPoiId
                            clusterMarkers
                            customPoiMarkers={offerAdditionalPois}
                            mapBottomSlot={mapBottomSlot}
                            css={mapCustomStyles}
                            onFullscreenClick={openMapModal}
                        />
                    ) : (
                        <Loader />
                    )}
                </div>

                {hasCountryPois && (
                    <div css={contentWrapperStyle}>
                        <OfferDetailLocationSummary {...props} routes={routes} />

                        <Button variant="filled_primary" css={[w100, mt(3)]} onClick={openMapModal}>
                            Sprawdź na mapie
                        </Button>
                    </div>
                )}
            </div>

            <Accordion id={PROPERTY_CHAT_BOT_TRIGGER}>
                <Accordion.Header>
                    <Text as="h3" variant="headline_6">
                        Przeczytaj więcej o lokalizacji
                    </Text>
                </Accordion.Header>
                <Accordion.Content>
                    {props.offer.location_description && (
                        <Text css={[mt(0), preLine]} mb="1.6rem" variant="body_copy_1">
                            {props.offer.location_description}
                        </Text>
                    )}

                    {props.offer.region.included_location?.description && (
                        <Text css={preLine} variant="body_copy_1">
                            {props.offer.region.included_location.description}
                        </Text>
                    )}
                </Accordion.Content>
            </Accordion>
        </>
    );
};

const wrapperStyle = css`
    background: #fff;
    ${borderRadius(2)};
    ${pv(2)};
    ${mb(2)};

    ${onDesktop(css`
        ${pv(3)};
    `)};
`;

const headerStyle = css`
    ${ph(1.5)};
    ${mb(2)};

    ${onDesktop(css`
        ${ph(3)};
    `)};
`;

const mapWrapperStyle = css`
    position: relative;
    height: 52rem;
    display: flex;
    justify-content: center;
    align-items: center;

    ${onDesktop(css`
        height: 41rem;
        ${mh(3)};
    `)};
`;

const contentWrapperStyle = css`
    ${ph(1.5)};

    ${onDesktop(css`
        ${ph(3)};
    `)};
`;

const preLine = css`
    white-space: pre-line;
`;

const mapCustomStyles = css`
    .leaflet-div-icon {
        background-color: transparent;
        border: none;
    }
`;
